import type { Data } from '@repo/strapi-types'
import AppLink from '@/components/elementary/AppLink'
import { NavbarMenuLink } from '@/components/elementary/navbar/NavbarMenuLink'
import { ArrowRight } from '@/components/icons/ArrowRight'
import { Paragraph } from '@/components/typography/Paragraph'
import { cn } from '@/lib/styles'
import { NAVBAR_TEST_IDS } from '@/lib/test-ids'
import styles from './NavbarColumnGroup.module.css'
import navbarMenuLinkStyles from './NavbarMenuLink.module.css'

interface NavbarColumnGroupProps {
	readonly group: Data.Component<'layout-utilities.navbar-column-group'>
	readonly columnTitle?: string | null
	readonly testId?: string
	readonly className?: string
	onLinkClick?: () => void
}

type Layout = 'default' | '2-columns' | '3-columns'

const layoutStyles: Record<Layout, string> = {
	default: 'gap-5 lg:gap-3',
	'2-columns': 'gap-4 lg:grid-cols-2 lg:gap-x-6 lg:gap-y-3',
	'3-columns': 'gap-4 lg:grid-cols-3 lg:gap-x-6 lg:gap-y-3',
}

export function NavbarColumnGroup({
	group,
	columnTitle,
	testId,
	className,
	onLinkClick,
}: NavbarColumnGroupProps) {
	// Type assertion needed until Strapi types are regenerated
	const layout = ((group as { layout?: Layout }).layout ?? 'default') as Layout
	const isMultiColumn = layout !== 'default'

	return (
		<div data-test-id={testId} className={cn('grid gap-5 h-fit lg:pb-2 lg:last:pb-0', className)}>
			{/* Column title - mobile only */}
			{columnTitle && (
				<Paragraph fontSize="headingSubtitle" textColor="gray" className="uppercase lg:hidden">
					{columnTitle}
				</Paragraph>
			)}

			{group?.title && (
				<Paragraph fontSize="headingMedium" className={styles['nav-group-title']}>
					{group?.title}
				</Paragraph>
			)}

			<div className={cn('grid', layoutStyles[layout])}>
				{group?.links?.map((link) => (
					<NavbarMenuLink
						key={link?.id}
						link={link}
						stackIcon={isMultiColumn}
						testId={NAVBAR_TEST_IDS.CATEGORY_COLUMN_LINK}
						onClick={onLinkClick}
					/>
				))}
			</div>

			{group?.moreLink?.href && group?.moreLink?.title ? (
				<AppLink
					href={group?.moreLink?.href}
					openExternalInNewTab={group?.moreLink?.newTab ?? false}
					variant="primaryLink"
					underline
					className="w-fit"
					iconEnd={<ArrowRight className="size-3 lg:size-4 text-brand-black" />}
					data-test-id={NAVBAR_TEST_IDS.CATEGORY_COLUMN_SEE_MORE}
					onClick={onLinkClick}
				>
					<Paragraph className={navbarMenuLinkStyles['nav-content-menu-arrow-link']}>
						{group?.moreLink?.title}
					</Paragraph>
				</AppLink>
			) : null}
		</div>
	)
}
